import { EFinancingProgram } from '@if/core/types'
import { MechanicalWarrantyComputeRequestDto } from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/ComputeMechanicalWarrantyRequestDto'
import { MechanicalWarrantyComputeResultDto } from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/ComputeMechanicalWarrantyResultDto'
import {
  MechanicalWarrantyDecisionDTO,
  MechanicalWarrantyWorksheet,
  MechanicalWarrantyWorksheetDto,
} from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/mechanicalWarrantyWorksheetSchema'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const baseUrl = '/Applications/mechwarranty/'
const worksheetUrl = `${baseUrl}{creditApplicationId}/Worksheet`
const calculatorUrl = `${baseUrl}Calculator/Compute`

// Query Keys
export const mechanicalWarrantyWorksheetKeys = {
  all: ['mechanicalWarrantyWorksheet'] as const,
}

// API Functions
export async function createMechanicalWarrantyWorksheet(
  data: MechanicalWarrantyWorksheetDto,
): Promise<MechanicalWarrantyWorksheet> {
  const apiClient = getApiClient()
  const url = worksheetUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.post<MechanicalWarrantyWorksheet>(url, data)
  return response.data
}

export async function updateMechanicalWarrantyWorksheet(
  data: MechanicalWarrantyWorksheetDto,
): Promise<MechanicalWarrantyWorksheet> {
  const apiClient = getApiClient()
  const url = worksheetUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<MechanicalWarrantyWorksheet>(url, data)
  return response.data
}

export async function computeMechanicalWarrantyLoan(
  data: MechanicalWarrantyComputeRequestDto,
): Promise<MechanicalWarrantyComputeResultDto> {
  const apiClient = getApiClient()
  const response = await apiClient.post<MechanicalWarrantyComputeResultDto>(calculatorUrl, data)
  return response.data
}

export async function updateMechanicalWarrantyWorksheetDecision(
  data: MechanicalWarrantyDecisionDTO,
): Promise<MechanicalWarrantyWorksheet> {
  const apiClient = getApiClient()
  const url = `${worksheetUrl}/Decision`.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<MechanicalWarrantyWorksheet>(url, data)
  return response.data
}

// React-Query Hooks
export function useCreateMechanicalWarrantyWorksheet(): [
  MutationFunction<MechanicalWarrantyWorksheet, MechanicalWarrantyWorksheetDto>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createMechanicalWarrantyWorksheet,
    onSuccess: (createdWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(
        createdWorksheet.creditApplicationId,
        EFinancingProgram.MechanicalWarranty,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: createdWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateMechanicalWarrantyWorksheet(): [
  MutationFunction<MechanicalWarrantyWorksheet, MechanicalWarrantyWorksheetDto>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateMechanicalWarrantyWorksheet,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(
        updatedWorksheet.creditApplicationId,
        EFinancingProgram.MechanicalWarranty,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useComputeMechanicalWarrantyLoan(): [
  MutationFunction<MechanicalWarrantyComputeResultDto, MechanicalWarrantyComputeRequestDto>,
  boolean,
] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: computeMechanicalWarrantyLoan,
  })

  return [mutateAsync, isPending]
}

export function useUpdateMechanicalWarrantyWorksheetDecision(): [
  MutationFunction<MechanicalWarrantyWorksheet, MechanicalWarrantyDecisionDTO>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateMechanicalWarrantyWorksheetDecision,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(
        updatedWorksheet.creditApplicationId,
        EFinancingProgram.MechanicalWarranty,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}
